import React, { useState } from "react"

import {
  Button,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Textarea,
  Tooltip,
} from "@coval/ui"
import { AlertCircle, Check, Pencil } from "lucide-react"

import { logger } from "@/lib/logger"
import type { RunRowLike } from "@/types/RunRow"

import { useConversationUpdateRunNotesTransport } from "../routes/useConversationRunActionsTransport"

const log = logger("ConversationNotesInput")

const NOTE_STATE = {
  IDLE: "idle",
  EDITING: "editing",
  SAVING: "saving",
  SAVE_SUCCESS: "save-success",
  SAVE_FAIL: "save-fail",
} as const

type NoteState = (typeof NOTE_STATE)[keyof typeof NOTE_STATE]

const NOTE_STATE_LABEL: Partial<Record<NoteState, string>> = {
  [NOTE_STATE.SAVING]: "Saving...",
  [NOTE_STATE.SAVE_SUCCESS]: "Saved",
  [NOTE_STATE.SAVE_FAIL]: "Failed",
}

interface ConversationNotesInputProps {
  item: RunRowLike
}

export const ConversationNotesInput: React.FC<ConversationNotesInputProps> = ({
  item,
}) => {
  const [textValue, setTextValue] = useState<string>(item.notes || "")
  const [noteState, setNoteState] = useState<NoteState>(NOTE_STATE.IDLE)
  const [error, setError] = useState<string | null>(null)
  const [isOpen, setIsOpen] = useState(false)

  const { mutateAsync: updateNotes } = useConversationUpdateRunNotesTransport()

  const saveToDatabase = async (): Promise<void> => {
    setNoteState(NOTE_STATE.SAVING)
    try {
      await updateNotes({
        model_output_id: item.simulation_output_id,
        notes: textValue,
      })
      setNoteState(NOTE_STATE.SAVE_SUCCESS)
      setTimeout(() => {
        setNoteState(NOTE_STATE.IDLE)
        setIsOpen(false)
      }, 500)
    } catch (error) {
      log.error("Error saving data", error)
      setNoteState(NOTE_STATE.SAVE_FAIL)
      setError("Could not save note")
      setTimeout(() => {
        setNoteState(NOTE_STATE.IDLE)
      }, 2000)
    }
  }

  const handleSave = (): void => {
    void saveToDatabase()
  }

  const handleKeyDown = (e: React.KeyboardEvent): void => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault()
      handleSave()
    }
  }

  const hasNotes = Boolean(textValue?.trim())

  return (
    <div role="presentation" onClick={(e) => e.stopPropagation()}>
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <Button icon variant="ghost" className="w-6 h-6" tooltip="Edit notes">
            <Tooltip content={hasNotes ? textValue : "Add note"}>
              <Pencil
                className={`w-3.5 h-3.5 ${hasNotes ? "text-coval-info-text" : "text-coval-text-muted"}`}
              />
            </Tooltip>
          </Button>
        </PopoverTrigger>
        <PopoverContent side="left" className="w-72">
          <div className="flex flex-col gap-2">
            <Textarea
              value={textValue || ""}
              placeholder="Add notes here..."
              onChange={(e) => {
                setTextValue(e.target.value)
                setError(null)
              }}
              onKeyDown={handleKeyDown}
              error={error != null}
              rows={2}
              aria-label="Notes"
            />
            {error && (
              <p className="text-xs text-coval-danger-text" role="alert">
                {error}
              </p>
            )}
            <div className="flex justify-end gap-2">
              <Button
                onClick={handleSave}
                disabled={noteState === NOTE_STATE.SAVING}
                aria-busy={noteState === NOTE_STATE.SAVING}
              >
                {noteState === NOTE_STATE.SAVE_SUCCESS ? (
                  <Check className="w-3 h-3" />
                ) : noteState === NOTE_STATE.SAVE_FAIL ? (
                  <AlertCircle className="w-3 h-3" />
                ) : null}
                {NOTE_STATE_LABEL[noteState] ?? "Save"}
              </Button>
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  )
}
